import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { DomainRedirect } from "@/components/DomainRedirect";

// Pages
import { UniversalLoginPage } from "@/pages/UniversalLoginPage";
import { LoginPage } from "@/pages/LoginPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { VerifyEmailPage, PendingVerificationPage } from "@/pages/VerifyEmailPage";
import { InvitationPage } from "@/pages/InvitationPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { JoinPage } from "@/pages/JoinPage";
import DirectLeadPage from "@/pages/DirectLeadPage";
import { CustomerDashboard } from "@/pages/CustomerDashboard";
import { SubmitReferralPage } from "@/pages/SubmitReferralPage";
import { RewardsPage } from "@/pages/RewardsPage";
import { CustomerTermsPage } from "@/pages/CustomerTermsPage";
import { AdminInvitationsPage } from "@/pages/AdminInvitationsPage";
import { AdminReferralsPage } from "@/pages/AdminReferralsPage";
import { AdminCustomersPage } from "@/pages/AdminCustomersPage";
import { AdminRewardsPage } from "@/pages/AdminRewardsPage";
import { AdminRewardSectionsPage } from "@/pages/AdminRewardSectionsPage";
import { AdminRedemptionsPage } from "@/pages/AdminRedemptionsPage";
import { AdminAnalyticsPage } from "@/pages/AdminAnalyticsPage";
import { AdminSettingsPage } from "@/pages/AdminSettingsPage";
import { AdminSalesRepsPage } from "@/pages/Admin/AdminSalesRepsPage";
import { SalesRepDashboard } from "@/pages/SalesRepDashboard";
import { SuperadminDashboard } from "@/pages/SuperadminDashboard";
import { SuperadminOrganizationsPage } from "@/pages/SuperadminOrganizationsPage";
import { SuperadminOrgDetailPage } from "@/pages/SuperadminOrgDetailPage";
import { ActivateSalesRepPage } from "@/pages/ActivateSalesRepPage";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRoles }) => {
    const { user, loading, isAuthenticated } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-pulse text-slate-400">Laden...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (requiredRoles && !requiredRoles.includes(user?.role)) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Tenant Route Wrapper with Domain Redirect
const TenantRoute = ({ children }) => {
    const { orgCode } = useParams();
    
    return (
        <DomainRedirect>
            <TenantProvider organizationCode={orgCode}>
                {children}
            </TenantProvider>
        </DomainRedirect>
    );
};

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    {/* Universal Login (Landing) */}
                    <Route path="/" element={<UniversalLoginPage />} />
                    <Route path="/login" element={<UniversalLoginPage />} />
                    
                    {/* Superadmin Routes */}
                    <Route path="/superadmin/dashboard" element={
                        <ProtectedRoute requiredRoles={['superadmin']}>
                            <SuperadminDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/superadmin/organizations" element={
                        <ProtectedRoute requiredRoles={['superadmin']}>
                            <SuperadminOrganizationsPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/superadmin/organizations/:orgId" element={
                        <ProtectedRoute requiredRoles={['superadmin']}>
                            <SuperadminOrgDetailPage />
                        </ProtectedRoute>
                    } />
                    
                    {/* Tenant Auth Routes */}
                    <Route path="/o/:orgCode/login" element={
                        <TenantRoute>
                            <LoginPage />
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/register" element={
                        <TenantRoute>
                            <RegisterPage />
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/invite" element={
                        <TenantRoute>
                            <InvitationPage />
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/forgot-password" element={
                        <TenantRoute>
                            <ForgotPasswordPage />
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/reset-password" element={
                        <TenantRoute>
                            <ResetPasswordPage />
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/verify" element={
                        <TenantRoute>
                            <VerifyEmailPage />
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/pending-verification" element={
                        <TenantRoute>
                            <PendingVerificationPage />
                        </TenantRoute>
                    } />
                    
                    {/* Sales Rep Activation */}
                    <Route path="/o/:orgCode/activate-sales" element={
                        <TenantRoute>
                            <ActivateSalesRepPage />
                        </TenantRoute>
                    } />
                    
                    {/* Public Join Page (new invite system) */}
                    <Route path="/join" element={<JoinPage />} />
                    
                    {/* Public Direct Lead Page (QR code without registration) - No TenantRoute to avoid redirect */}
                    <Route path="/o/:orgCode/lead" element={<DirectLeadPage />} />
                    
                    {/* Customer Routes */}
                    <Route path="/o/:orgCode/dashboard" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['customer', 'org_admin']}>
                                <CustomerDashboard />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/submit-referral" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['customer', 'org_admin']}>
                                <SubmitReferralPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/rewards" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['customer', 'org_admin']}>
                                <RewardsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/terms" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['customer', 'org_admin']}>
                                <CustomerTermsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    
                    {/* Admin Routes */}
                    <Route path="/o/:orgCode/admin/invitations" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['org_admin']}>
                                <AdminInvitationsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/admin/referrals" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['org_admin']}>
                                <AdminReferralsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/admin/customers" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['org_admin']}>
                                <AdminCustomersPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/admin/rewards" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['org_admin']}>
                                <AdminRewardsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/admin/reward-sections" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['org_admin']}>
                                <AdminRewardSectionsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/admin/redemptions" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['org_admin']}>
                                <AdminRedemptionsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/admin/analytics" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['org_admin']}>
                                <AdminAnalyticsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/admin/settings" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['org_admin']}>
                                <AdminSettingsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    <Route path="/o/:orgCode/admin/sales-reps" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['org_admin']}>
                                <AdminSalesRepsPage />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    
                    {/* Sales Rep Routes */}
                    <Route path="/o/:orgCode/sales" element={
                        <TenantRoute>
                            <ProtectedRoute requiredRoles={['sales_rep']}>
                                <SalesRepDashboard />
                            </ProtectedRoute>
                        </TenantRoute>
                    } />
                    
                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
        </AuthProvider>
    );
}

export default App;
